import { formatNumber, formatByKind, getFkText } from './chartFormats';
import { computeColumnStats, applyConditional } from './gridCellFormats';

const DEFAULT_PAGE_SIZE = 20;
const TOTALS_RESOURCE_KEYS = {
    sum: 'GridChartTotalsSum',
    avg: 'GridChartTotalsAvg',
    count: 'GridChartTotalsCount',
    min: 'GridChartTotalsMin',
    max: 'GridChartTotalsMax'
};

export default class GridChartRenderer {
    constructor(container, chart, callbacks) {
        this.container = container;
        this.chart = chart;
        this.callbacks = callbacks || {};
        this.page = 0;
        this.sort = chart.sort || null;
        this.rows = [];
        this._collapsed = new Set();
    }

    updateChart(chart) {
        this.chart = chart;
        if (chart.sort !== undefined) this.sort = chart.sort || null;
    }

    setRows(rows) {
        this.rows = rows || [];
        this._stats = computeColumnStats(this.rows, this.chart.columns || []);
        this._totals = this._computeTotals(this.rows, this.chart.columns || []);
        this._groups = this._computeGroups(this.rows, this.chart);
        let total = this._totalPages();
        if (this.page >= total) this.page = total - 1;
        if (this.page < 0) this.page = 0;
        this._render();
    }

    _isGrouped() {
        return !!(this.chart.groupBy && this._groups);
    }

    _computeGroups(rows, chart) {
        if (!chart.groupBy) return null;
        let groupField = chart.groupBy;
        let map = new Map();
        let order = [];
        rows.forEach(row => {
            let fk = getFkText(row, groupField);
            let val = fk != null ? fk : row.get(groupField);
            let key = (val == null || val === '') ? '—' : String(val);
            let g = map.get(key);
            if (!g) {
                g = { key: key, rows: [] };
                map.set(key, g);
                order.push(g);
            }
            g.rows.push(row);
        });
        order.forEach(g => {
            g.totals = this._computeTotals(g.rows, chart.columns || []);
        });
        return order;
    }

    _computeTotals(rows, columns) {
        let totals = {};
        let anyEnabled = false;
        columns.forEach(col => {
            let kind = col && col.totals;
            if (!kind || kind === 'none' || !col.field) return;
            anyEnabled = true;

            let count = 0, numCount = 0, sum = 0;
            let min = null, max = null;
            rows.forEach(row => {
                let fk = getFkText(row, col.field);
                let val = fk != null ? fk : row.get(col.field);
                if (val == null || val === '') return;
                count++;
                let n = typeof val === 'number' ? val : Number(val);
                if (!isNaN(n) && fk == null) {
                    numCount++;
                    sum += n;
                    if (min == null || n < min) min = n;
                    if (max == null || n > max) max = n;
                }
            });

            let value = null;
            switch (kind) {
                case 'count': value = count; break;
                case 'sum':   value = numCount > 0 ? sum : null; break;
                case 'avg':   value = numCount > 0 ? sum / numCount : null; break;
                case 'min':   value = min; break;
                case 'max':   value = max; break;
            }
            totals[col.field] = { kind, value };
        });
        totals._anyEnabled = anyEnabled;
        return totals;
    }

    _render() {
        this.container.textContent = '';

        let wrap = document.createElement('div');
        wrap.className = 'xp-grid-chart';
        if (this.chart.density === 'comfortable') wrap.classList.add('is-comfortable');
        else wrap.classList.add('is-compact');
        if (this.chart.stickyFirstColumn) wrap.classList.add('is-sticky-first');

        let headerSection = document.createElement('div');
        headerSection.className = 'xp-grid-chart-section xp-grid-chart-section-header';
        let headerTable = document.createElement('table');
        headerTable.className = 'xp-grid-chart-table';
        headerTable.appendChild(this._buildColgroup());
        headerTable.appendChild(this._buildHeader());
        headerSection.appendChild(headerTable);
        wrap.appendChild(headerSection);

        let bodySection = document.createElement('div');
        bodySection.className = 'xp-grid-chart-section xp-grid-chart-section-body';
        let bodyTable = document.createElement('table');
        bodyTable.className = 'xp-grid-chart-table';
        bodyTable.appendChild(this._buildColgroup());
        bodyTable.appendChild(this._buildBody());
        bodySection.appendChild(bodyTable);
        wrap.appendChild(bodySection);

        let bottomSection = document.createElement('div');
        bottomSection.className = 'xp-grid-chart-section xp-grid-chart-section-bottom';
        let totalsRow = this._buildTotalsRowIfAny();
        if (totalsRow) {
            let totalsTable = document.createElement('table');
            totalsTable.className = 'xp-grid-chart-table';
            totalsTable.appendChild(this._buildColgroup());
            let tfoot = document.createElement('tfoot');
            tfoot.appendChild(totalsRow);
            totalsTable.appendChild(tfoot);
            bottomSection.appendChild(totalsTable);
        }
        bottomSection.appendChild(this._buildPager());
        wrap.appendChild(bottomSection);

        this.container.appendChild(wrap);

        this._tables = { header: headerTable, body: bodyTable, totals: bottomSection.querySelector('table') };
        this._syncColumnWidths();
    }

    _buildColgroup() {
        let cg = document.createElement('colgroup');
        let columns = this.chart.columns || [];
        columns.forEach(col => {
            let c = document.createElement('col');
            if (col.width) {
                let w = parseInt(col.width, 10);
                if (w > 0) c.style.width = w + 'px';
            }
            cg.appendChild(c);
        });
        return cg;
    }

    _syncColumnWidths() {
        if (!this._tables) return;
        let body = this._tables.body;
        let firstRow = body && body.querySelector('tbody tr');
        if (!firstRow || firstRow.children.length === 0) return;

        let widths = Array.from(firstRow.children).map(td => Math.round(td.getBoundingClientRect().width));

        [this._tables.header, this._tables.body, this._tables.totals].forEach(t => {
            if (!t) return;
            let cg = t.querySelector('colgroup');
            if (!cg) return;
            let cols = cg.querySelectorAll('col');
            widths.forEach((w, i) => {
                if (cols[i]) cols[i].style.width = w + 'px';
            });
            t.style.tableLayout = 'fixed';
        });
    }

    _buildTotalsRowIfAny() {
        if (!this._totals || !this._totals._anyEnabled) return null;
        return this._buildTotalsRow(this._totals, this.chart.columns || []);
    }

    _buildTotalsRow(totals, columns) {
        let tr = document.createElement('tr');
        columns.forEach(col => {
            let td = document.createElement('td');
            td.className = 'xp-grid-chart-total';
            this._applyColStyle(td, col);
            let entry = totals[col.field];
            if (entry && entry.value != null) {
                let kind = entry.kind;
                let needsNumericFormat = kind === 'sum' || kind === 'avg' || kind === 'min' || kind === 'max' || kind === 'count';
                let display = typeof entry.value === 'number' && needsNumericFormat
                    ? formatNumber(entry.value, kind === 'avg' ? { decimals: 2 } : undefined)
                    : String(entry.value);
                let label = document.createElement('span');
                label.className = 'xp-grid-chart-total-kind';
                label.textContent = this._localizeKind(kind);
                td.appendChild(label);
                td.appendChild(document.createTextNode(' ' + display));
                td.classList.add('is-active');
            }
            if (col.align === 'auto' || !col.align) td.classList.add('is-numeric');
            tr.appendChild(td);
        });
        return tr;
    }

    _localizeKind(kind) {
        let R = this.callbacks.getResource;
        if (R && TOTALS_RESOURCE_KEYS[kind]) {
            let s = R(TOTALS_RESOURCE_KEYS[kind]);
            if (s && s !== TOTALS_RESOURCE_KEYS[kind]) return s;
        }
        return kind.charAt(0).toUpperCase() + kind.slice(1);
    }

    _buildHeader() {
        let thead = document.createElement('thead');
        let tr = document.createElement('tr');
        let columns = this.chart.columns || [];

        columns.forEach((col, index) => {
            let th = document.createElement('th');
            th.className = 'xp-grid-chart-th';
            this._applyColStyle(th, col);

            let label = document.createElement('span');
            label.className = 'xp-grid-chart-th-label';
            label.textContent = col.label || col.field || '';
            th.appendChild(label);

            let arrow = document.createElement('span');
            arrow.className = 'xp-grid-chart-th-arrow';
            if (this.sort && this.sort.field === col.field) {
                arrow.textContent = this.sort.dir === 'desc' ? '▼' : '▲';
                th.classList.add('is-sorted');
            }
            th.appendChild(arrow);

            th.addEventListener('click', (e) => {
                if (e.target && e.target.classList && e.target.classList.contains('xp-grid-chart-th-grip')) return;
                this._onSortClick(col.field);
            });

            let grip = document.createElement('span');
            grip.className = 'xp-grid-chart-th-grip';
            grip.addEventListener('mousedown', (e) => this._beginColumnResize(e, th, index));
            grip.addEventListener('click', (e) => e.stopPropagation());
            th.appendChild(grip);

            tr.appendChild(th);
        });

        thead.appendChild(tr);
        return thead;
    }

    _beginColumnResize(e, th, colIndex) {
        e.preventDefault();
        e.stopPropagation();
        let startX = e.clientX;
        let startW = th.getBoundingClientRect().width;

        document.body.classList.add('xp-grid-chart-resizing');

        let onMove = (ev) => {
            let next = Math.max(40, Math.round(startW + (ev.clientX - startX)));
            this._setColumnWidth(colIndex, next);
        };
        let onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            document.body.classList.remove('xp-grid-chart-resizing');
            let columns = this.chart.columns || [];
            let col = columns[colIndex];
            if (col && this.callbacks.onColumnResize) {
                this.callbacks.onColumnResize(colIndex, col.width);
            }
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    }

    _setColumnWidth(colIndex, width) {
        let columns = this.chart.columns || [];
        let col = columns[colIndex];
        if (!col) return;
        col.width = width;
        if (!this._tables) return;
        [this._tables.header, this._tables.body, this._tables.totals].forEach(t => {
            if (!t) return;
            let cols = t.querySelectorAll('colgroup col');
            if (cols[colIndex]) cols[colIndex].style.width = width + 'px';
        });
    }

    _applyColStyle(cell, col) {
        if (col.align && col.align !== 'auto') {
            cell.style.textAlign = col.align;
        }
    }

    _buildBody() {
        let tbody = document.createElement('tbody');
        let columns = this.chart.columns || [];

        if (columns.length === 0) {
            tbody.appendChild(this._emptyRow(columns));
            return tbody;
        }

        if (this._isGrouped()) {
            if (this._groups.length === 0) {
                tbody.appendChild(this._emptyRow(columns));
                return tbody;
            }
            this._groups.forEach(group => {
                tbody.appendChild(this._buildGroupHeaderRow(group, columns));
                if (this._collapsed.has(group.key)) return;
                let groupRows = this._sortRows(group.rows);
                groupRows.forEach(row => tbody.appendChild(this._buildDataRow(row, columns)));
            });
            return tbody;
        }

        let rows = this._sortRows(this.rows);
        let pageSize = this._pageSize();
        let start = this.page * pageSize;
        let pageRows = rows.slice(start, start + pageSize);
        if (pageRows.length === 0) {
            tbody.appendChild(this._emptyRow(columns));
            return tbody;
        }
        pageRows.forEach(row => tbody.appendChild(this._buildDataRow(row, columns)));
        return tbody;
    }

    _emptyRow(columns) {
        let tr = document.createElement('tr');
        let td = document.createElement('td');
        td.colSpan = Math.max(1, columns.length);
        td.className = 'xp-grid-chart-empty';
        td.textContent = '—';
        tr.appendChild(td);
        return tr;
    }

    _buildDataRow(row, columns) {
        let tr = document.createElement('tr');
        columns.forEach(col => {
            let td = document.createElement('td');
            this._applyColStyle(td, col);
            let fkText = col.field ? getFkText(row, col.field) : null;
            let rawField = col.field ? row.get(col.field) : null;
            let raw = fkText != null ? fkText : rawField;
            let kind = col.format && col.format.kind;

            if (kind === 'sparkline' && fkText == null) {
                this._renderSparklineCell(td, rawField);
                tr.appendChild(td);
                return;
            }

            let display = this._getCellDisplay(raw, col);
            let handled = false;
            if (fkText == null) {
                handled = applyConditional(td, raw, display, col, this._stats);
            }
            if (!handled) {
                if (fkText != null && (!kind || kind === 'auto' || kind === 'text')) {
                    let chip = document.createElement('span');
                    chip.className = 'xp-grid-chart-chip';
                    chip.textContent = String(fkText);
                    td.appendChild(chip);
                } else if (display === '' || display == null) {
                    td.classList.add('is-empty');
                    td.textContent = '—';
                } else {
                    td.textContent = display;
                }
            }
            let isNumeric = kind === 'number' || kind === 'percent' || ((!kind || kind === 'auto') && typeof raw === 'number' && fkText == null);
            if (isNumeric && (!col.align || col.align === 'auto')) td.classList.add('is-numeric');
            tr.appendChild(td);
        });
        this._bindRowInteractions(tr, row);
        return tr;
    }

    _renderSparklineCell(td, rawValue) {
        if (rawValue == null || rawValue === '') {
            td.classList.add('is-empty');
            td.textContent = '—';
            return;
        }
        let parts;
        if (Array.isArray(rawValue)) parts = rawValue;
        else parts = String(rawValue).split(/[,;|\s]+/);
        let nums = [];
        parts.forEach(p => {
            if (p === '' || p == null) return;
            let n = Number(p);
            if (!isNaN(n)) nums.push(n);
        });
        if (nums.length < 2) {
            td.textContent = nums.length === 1 ? String(nums[0]) : '—';
            if (nums.length === 0) td.classList.add('is-empty');
            return;
        }

        let min = Math.min(...nums);
        let max = Math.max(...nums);
        let range = max - min || 1;
        const W = 80, H = 18, pad = 1;
        let stepX = (W - pad * 2) / (nums.length - 1);
        let coords = nums.map((n, i) => {
            let x = pad + i * stepX;
            let y = H - pad - ((n - min) / range) * (H - pad * 2);
            return x.toFixed(1) + ',' + y.toFixed(1);
        });
        let path = 'M' + coords.join(' L');

        let svgNs = 'http://www.w3.org/2000/svg';
        let svg = document.createElementNS(svgNs, 'svg');
        svg.setAttribute('class', 'xp-grid-chart-spark');
        svg.setAttribute('viewBox', '0 0 ' + W + ' ' + H);
        svg.setAttribute('preserveAspectRatio', 'none');

        let line = document.createElementNS(svgNs, 'path');
        line.setAttribute('d', path);
        line.setAttribute('class', 'xp-grid-chart-spark-line');
        svg.appendChild(line);

        let last = coords[coords.length - 1].split(',');
        let dot = document.createElementNS(svgNs, 'circle');
        dot.setAttribute('cx', last[0]);
        dot.setAttribute('cy', last[1]);
        dot.setAttribute('r', '1.5');
        dot.setAttribute('class', 'xp-grid-chart-spark-dot');
        svg.appendChild(dot);

        td.appendChild(svg);
        td.classList.add('has-spark');
    }

    _buildGroupHeaderRow(group, columns) {
        let tr = document.createElement('tr');
        tr.className = 'xp-grid-chart-group-header';
        let collapsed = this._collapsed.has(group.key);
        if (collapsed) tr.classList.add('is-collapsed');

        let groupBy = this.chart.groupBy;
        let labelColIndex = columns.findIndex(c => c.field === groupBy);
        if (labelColIndex < 0) labelColIndex = 0;

        columns.forEach((col, i) => {
            let td = document.createElement('td');
            this._applyColStyle(td, col);

            if (i === labelColIndex) {
                let arrow = document.createElement('span');
                arrow.className = 'xp-grid-chart-group-arrow';
                arrow.textContent = collapsed ? '▶' : '▼';
                td.appendChild(arrow);
                let lbl = document.createElement('span');
                lbl.className = 'xp-grid-chart-group-label';
                lbl.textContent = group.key;
                td.appendChild(lbl);
                let cnt = document.createElement('span');
                cnt.className = 'xp-grid-chart-group-count';
                cnt.textContent = '(' + group.rows.length + ')';
                td.appendChild(cnt);
            } else {
                let entry = group.totals && group.totals[col.field];
                if (entry && entry.value != null) {
                    let kind = entry.kind;
                    let needsNumericFormat = kind === 'sum' || kind === 'avg' || kind === 'min' || kind === 'max' || kind === 'count';
                    let display = typeof entry.value === 'number' && needsNumericFormat
                        ? formatNumber(entry.value, kind === 'avg' ? { decimals: 2 } : undefined)
                        : String(entry.value);
                    let label = document.createElement('span');
                    label.className = 'xp-grid-chart-total-kind';
                    label.textContent = this._localizeKind(kind);
                    td.appendChild(label);
                    td.appendChild(document.createTextNode(' ' + display));
                }
                if (col.align === 'auto' || !col.align) td.classList.add('is-numeric');
            }
            tr.appendChild(td);
        });

        tr.addEventListener('click', () => {
            if (this._collapsed.has(group.key)) this._collapsed.delete(group.key);
            else this._collapsed.add(group.key);
            this._render();
        });

        return tr;
    }

    _buildPager() {
        let pager = document.createElement('div');
        pager.className = 'xp-grid-chart-pager';

        if (this._isGrouped()) {
            let info = document.createElement('span');
            info.className = 'xp-grid-chart-page-info';
            info.textContent = this._groups.length + ' / ' + this.rows.length;
            pager.appendChild(info);
            return pager;
        }

        let pageSize = this._pageSize();
        let total = this.rows.length;
        let pages = this._totalPages();
        let manyPages = pages > 1;

        let mkBtn = (text, target, disabled) => {
            let b = document.createElement('button');
            b.type = 'button';
            b.className = 'xp-grid-chart-page-btn';
            b.textContent = text;
            b.disabled = disabled;
            b.addEventListener('click', () => this._gotoPage(target));
            return b;
        };

        pager.appendChild(mkBtn('«', 0, this.page === 0 || !manyPages));
        pager.appendChild(mkBtn('‹', this.page - 1, this.page === 0 || !manyPages));

        let info = document.createElement('span');
        info.className = 'xp-grid-chart-page-info';
        if (total === 0) {
            info.textContent = '0';
        } else {
            let start = this.page * pageSize + 1;
            let end = Math.min(start + pageSize - 1, total);
            info.textContent = start + '–' + end + ' / ' + total;
        }
        pager.appendChild(info);

        pager.appendChild(mkBtn('›', this.page + 1, this.page >= pages - 1 || !manyPages));
        pager.appendChild(mkBtn('»', pages - 1, this.page >= pages - 1 || !manyPages));

        return pager;
    }

    _onSortClick(field) {
        if (!field) return;
        let cur = this.sort;
        let next;
        if (!cur || cur.field !== field) next = { field, dir: 'asc' };
        else if (cur.dir === 'asc') next = { field, dir: 'desc' };
        else next = null;
        this.sort = next;
        this.page = 0;
        this._render();
    }

    _gotoPage(p) {
        let pages = this._totalPages();
        if (p < 0) p = 0;
        if (p >= pages) p = pages - 1;
        if (p === this.page) return;
        this.page = p;
        this._render();
    }

    _pageSize() {
        let n = parseInt(this.chart.rowsPerPage, 10);
        if (!n || n < 1) return DEFAULT_PAGE_SIZE;
        return n;
    }

    _totalPages() {
        return Math.max(1, Math.ceil(this.rows.length / this._pageSize()));
    }

    _sortRows(arr) {
        if (!this.sort) return arr;
        let rows = arr.slice();
        let field = this.sort.field;
        let mul = this.sort.dir === 'desc' ? -1 : 1;
        rows.sort((a, b) => {
            let av = this._getCellRaw(a, field);
            let bv = this._getCellRaw(b, field);
            if (av == null && bv == null) return 0;
            if (av == null) return 1;
            if (bv == null) return -1;
            if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * mul;
            return String(av).localeCompare(String(bv)) * mul;
        });
        return rows;
    }

    _bindRowInteractions(tr, feature) {
        let cb = this.callbacks;
        if (cb.onRowClick) {
            tr.classList.add('is-clickable');
            tr.addEventListener('click', () => cb.onRowClick(feature));
        }
        if (cb.onRowEnter || cb.onRowLeave) {
            tr.addEventListener('mouseenter', () => {
                if (cb.onRowEnter) tr._oldState = cb.onRowEnter(feature);
            });
            tr.addEventListener('mouseleave', () => {
                if (cb.onRowLeave) cb.onRowLeave(feature, tr._oldState);
                tr._oldState = null;
            });
        }
    }

    toCsv() {
        let columns = this.chart.columns || [];
        let escape = (val) => {
            let s = val == null ? '' : String(val);
            if (/["\n\r,;]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
            return s;
        };
        let lines = ['sep=,'];
        lines.push(columns.map(c => escape(c.label || c.field || '')).join(','));

        let pushRow = (row) => {
            let cells = columns.map(col => {
                let raw = this._getCellRaw(row, col.field);
                return escape(this._getCellDisplay(raw, col));
            });
            lines.push(cells.join(','));
        };

        if (this._isGrouped()) {
            this._groups.forEach(g => this._sortRows(g.rows).forEach(pushRow));
        } else {
            this._sortRows(this.rows).forEach(pushRow);
        }
        return lines.join('\r\n');
    }

    _getCellRaw(row, field) {
        if (!field) return null;
        let fk = getFkText(row, field);
        if (fk != null) return fk;
        let val = row.get(field);
        return val != null ? val : null;
    }

    _getCellDisplay(raw, col) {
        if (raw == null) return '';
        let kind = col && col.format && col.format.kind;
        if (!kind || kind === 'auto') {
            return typeof raw === 'number' ? formatNumber(raw) : String(raw);
        }
        return formatByKind(raw, kind);
    }
}
